/** Tab 9: Skill / Experience */
import { useState } from 'react'
import {
  Button, Card, Form, Input, message, Space, Select, InputNumber, Row, Col, Tooltip, Tag,
} from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { SKILL_CLASSES, SKILL_CLASS_COLOR } from '@/features/hr/skillMismatchApi'

interface Props { detail: JoiningDetail }

export default function SkillTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="Skill & Experience" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="qualification" label="Qualification">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="technical_qualification" label="Technical Qualification">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="previous_experience_years" label="Previous Experience (years)">
              <InputNumber style={{ width: '100%' }} step={0.5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="previous_employer" label="Previous Employer">
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="skill_category"
              label="Skill Category"
              tooltip="Functional category — e.g. OPERATOR / FITTER / FOREMAN / JOBBER. Free text for now."
            >
              <Input placeholder="e.g. OPERATOR" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="trade_type"
              label="Trade Type"
              tooltip="ITI trade / formal qualification — e.g. ELECTRICIAN, FITTER, TURNER."
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="worker_skill_class"
              label={(
                <Tooltip title="Current operational skill class. Drives the Skill Mismatch system at worker allocation time. Edit in bulk via HR → Worker Skills.">
                  <span>Skill Class <span style={{ fontSize: 11, color: '#1677ff' }}>(live)</span></span>
                </Tooltip>
              )}
            >
              <Select
                options={SKILL_CLASSES.map((c) => ({
                  value: c,
                  label: (
                    <span>
                      <Tag color={SKILL_CLASS_COLOR[c]} style={{ marginRight: 4 }}>{c}</Tag>
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="machine_experience" label="Machine Experience">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
